import * as React from 'react'

import ProjectDialog from '../../components/ProjectDialog'
import Contact from '../../components/Contact'
import Comment from '../../components/Comment'

import { Project, ProjectId } from '../../interfaces/entities/project'
import { Descriptive, Labeled } from '../../interfaces/base'
import { User } from '../../interfaces/entities/user'
import {
  Box,
  Typography,
  Tooltip,
  Paper,
  Grid,
  Divider,
  Icon,
  styled,
} from '@design/pt-ui'
import EditButton from '../../components/EditButton'
import CreativeStatuses from '../../components/CreativeStatuses'
import ProjectActionButton from '../../components/ProjectActionButton'
import { translateWithUserLocale } from '../../utils/i18n'

const InfoBox = styled(Paper)(({ theme }) => ({
  padding: `0 ${theme.spacing(2)}`,
  minHeight: '100%',
}))

const HeaderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 0,
  paddingTop: theme.spacing(1),
  minHeight: 35,
  '& div': {
    display: 'none',
  },
}))

const Column = styled('div')({
  display: 'flex',
  flexFlow: 'column',
  '& span': {
    fontWeight: 400,
  },
})

const Title = styled('h4')({
  margin: 0,
})

interface IHeaderProps {
  project: Project | null
  creativeStates: Labeled[]
  user: User
  clickOnProjectAction: (action: Descriptive, project: Project) => void
  onProjectSave: (project: Project, meta: { created: boolean }) => any
  setProjectComment: (projectId: ProjectId, comment: string) => any
  getProject: (id: ProjectId) => void
}

function Header(props: IHeaderProps) {
  const handleComment = (comment: string) => {
    if (comment !== null && props.project !== null) {
      props.setProjectComment(props.project.id, comment)
    }
  }

  if (props.project == null) {
    return null
  }

  const archivedProject = props.project.status.name === 'archived'

  return (
    <Grid container={true} spacing={2}>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}>
        <InfoBox>
          <HeaderRow>
            <Title>{translateWithUserLocale('project.project_details')}</Title>
            {props.user.groups.includes('client') || archivedProject ? null : (
              <ProjectDialog
                onSave={async (project: any) => {
                  props.onProjectSave(project as Project, {
                    created: true,
                  })
                }}
                activator={<EditButton />}
                project={props.project}
              />
            )}
          </HeaderRow>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              height: '100%',
            }}>
            <Column>
              <h4>{translateWithUserLocale('project.campaign_numbers')}</h4>
              <span>
                {props.project.campaigns
                  .map((campaign) => campaign.projectNumber)
                  .join(', ')}
              </span>
            </Column>
            <Column>
              <h4>{translateWithUserLocale('project.delivery_deadline')}</h4>
              <span>{props.project.deadline.format('DD.MM.YYYY')}</span>
            </Column>
            <Column>
              <h4>
                {translateWithUserLocale('project.project_contact_client')}
              </h4>

              {props.project.contacts.map((contact) => (
                <Contact contact={contact} key={contact.id} />
              ))}
            </Column>
            <Column>
              <h4>{translateWithUserLocale('project.your_contact_person')}</h4>
              {props.project.pmContacts.map((contact) => (
                <Contact contact={contact} key={contact.id} />
              ))}
            </Column>
          </Box>
        </InfoBox>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}>
        <InfoBox>
          <HeaderRow>
            <Title>{translateWithUserLocale('project.project_status')}</Title>
          </HeaderRow>
          <Divider />
          <Box sx={{ display: 'flex' }}>
            <Grid
              size={{
                md: 4,
              }}>
              <Title>{translateWithUserLocale('project.overall_status')}</Title>
              <Box
                sx={{
                  display: 'flex',
                  paddingTop: '15px',
                  fontSize: '13px',
                }}>
                <Title as="p">{props.project.status.title}</Title>
                <Tooltip
                  title={props.project.status.description}
                  placement="top">
                  <Icon sx={{ marginLeft: '5px' }}>info</Icon>
                </Tooltip>
              </Box>
            </Grid>
            <Grid
              size={{
                md: 8,
              }}>
              <Title>
                {translateWithUserLocale('project.specific_status')}
              </Title>
              <CreativeStatuses
                project={props.project}
                states={props.creativeStates.filter(
                  (state) => state.name !== 'deleted',
                )}
              />
            </Grid>
          </Box>
        </InfoBox>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 2,
        }}>
        <ProjectActionButton
          project={props.project}
          user={props.user}
          clickOnProjectAction={props.clickOnProjectAction}
          getProject={props.getProject}
        />
      </Grid>
      {props.user.groups.includes('client') &&
      props.project.comment === null ? (
        ''
      ) : (
        <Grid
          size={{
            xs: 12,
            sm: 12,
          }}>
          <Typography variant="body2">
            {translateWithUserLocale('comment')}
          </Typography>

          <Comment
            comment={props.project.comment}
            disabled={props.user.groups.includes('client') || archivedProject}
            onSave={
              props.user.groups.includes('client') ? undefined : handleComment
            }
            showMessageIcon={false}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default Header
